<template>
  <div class="d-flex align-items-center">
    <span
      class="ml-4 mr-4 text-secondary text-center"
      style="width: 60px; word-wrap: break-word"
      >{{ disabled ? "???" : formatStock(currentStock) }}</span
    >
    <i class="fas fa-arrow-right mr-2 text-secondary"></i>
    <b-form-input
      inputmode="numeric"
      class="no-arrow text-center"
      style="max-width: 100px"
      :value="afterStock"
      :max="max"
      :min="min"
      @input="onInputChange"
      @blur="onBlur"
      :disabled="disabled"
      :class="{
        'is-invalid': invalid,
      }"
      @keydown="allowOnlyTwoDecimalNumber($event)"
    ></b-form-input>
  </div>
</template>

<script>
import "@/theme/modal/ProductAndStock/Product.scss";
import BigNumber from "bignumber.js";
import { validateTwoDecimal } from "@/utils/numberValidation";

export default {
  name: "PreAndPostStockField",
  components: {},
  props: {
    currentStock: {
      type: Number,
      default: 0,
    },
    adjustStock: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      userHasEdited: false,
      afterStock: Number(
        new BigNumber(this.currentStock)
          .plus(new BigNumber(this.adjustStock))
          .toNumber()
      ),
    };
  },
  validations: {},
  computed: {},
  methods: {
    formatStock(value) {
      if (value === null || value === "") return "";
      const fixed = new BigNumber(value).toFixed(2);
      return fixed.replace(/\.?0+$/, "");
    },
    onInputChange(value) {
      this.userHasEdited = true;
      this.afterStock = value;

      const raw = (value ?? "").toString().trim();
      if (raw === "") {
        this.onChange(new BigNumber(0));
        return;
      }

      const afterNum = new BigNumber(value);
      if (!afterNum.isFinite()) {
        return;
      }

      const adjustment = new BigNumber(value)
        .minus(new BigNumber(this.currentStock))
        .toFixed(2);
      this.onChange(adjustment);
    },
    onBlur() {
      let raw = (this.afterStock ?? "").toString().trim();

      if (raw === "") {
        const defaultAfter = new BigNumber(this.currentStock).plus(0);
        this.afterStock = !validateTwoDecimal(defaultAfter)
          ? defaultAfter.toFixed(2)
          : defaultAfter.toNumber();
        this.onChange(new BigNumber(0));
        return;
      }

      //  -0, -0.0, 0.0, 00.00, 000, 000.000
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);
      if (isZeroLike) {
        this.afterStock = "0";
        this.onChange(
          new BigNumber(this.afterStock)
            .minus(new BigNumber(this.currentStock))
            .toFixed(2)
        );
        return;
      }

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) {
        const defaultAfter = new BigNumber(this.currentStock).plus(
          new BigNumber(this.adjustStock).toFixed(2)
        );

        this.afterStock = !validateTwoDecimal(defaultAfter)
          ? defaultAfter.toFixed(2)
          : defaultAfter.toNumber();

        this.onChange(
          new BigNumber(this.afterStock)
            .minus(new BigNumber(this.currentStock))
            .toFixed(2)
        );
        return;
      }

      if (this.afterStock < this.min) {
        this.afterStock = new BigNumber(this.min).toNumber();
      } else if (this.afterStock > this.max) {
        this.afterStock = new BigNumber(this.max).toNumber();
      } else if (!validateTwoDecimal(this.afterStock)) {
        this.afterStock = new BigNumber(this.afterStock).toFixed(2);
      }
      this.onChange(
        new BigNumber(this.afterStock)
          .minus(new BigNumber(this.currentStock))
          .toFixed(2)
      );
    },
    allowOnlyTwoDecimalNumber(e) {
      if (e.ctrlKey || e.metaKey) {
        const k = (e.key || "").toLowerCase();
        const allowed = ["a", "c", "v", "x", "z", "y"];
        if (allowed.includes(k)) return;
      }
      if (e.shiftKey && e.key === "Insert") return;

      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];

      if (allowedKeys.includes(e.key)) return;

      const input = e.target;
      const value = input.value || "";
      const { selectionStart, selectionEnd } = input;

      // Minus: only at the very start
      if (e.key === "-") {
        if (selectionStart !== 0) {
          e.preventDefault();
        }
        return;
      }

      // One decimal point only
      if (e.key === ".") {
        if (value.includes(".")) {
          e.preventDefault();
        }
        return;
      }

      // Digits 0–9: allow only if result would have at most 2 decimal places
      if (/^\d$/.test(e.key)) {
        const newValue =
          value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
        const parts = newValue.split(".");
        if (parts.length === 2 && parts[1].length > 2) {
          e.preventDefault();
        }
        return;
      }
      e.preventDefault();
    },
  },
  watch: {
    adjustStock(newVal) {
      if (this.afterStock === "") return;

      const result = new BigNumber(this.currentStock).plus(
        new BigNumber(newVal)
      );
      const raw = String(this.afterStock).trim();
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);

      if (isZeroLike && this.userHasEdited) {
        if (raw.startsWith("-")) {
          return;
        }
        this.afterStock = 0;
        return;
      }

      this.userHasEdited = false;
      if (!validateTwoDecimal(result)) {
        this.afterStock = result.toFixed(2);
      } else {
        this.afterStock = result.toNumber();
      }
    },
    currentStock() {
      if (this.afterStock === "") return;

      const result = new BigNumber(this.currentStock).plus(
        new BigNumber(this.adjustStock)
      );
      const raw = String(this.afterStock).trim();
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);

      if (isZeroLike && this.userHasEdited) {
        if (raw.startsWith("-")) {
          return;
        }
        this.afterStock = 0;
        return;
      }

      this.userHasEdited = false;
      if (!validateTwoDecimal(result)) {
        this.afterStock = result.toFixed(2);
      } else {
        this.afterStock = result.toNumber();
      }
    },
  },
};
</script>

<style scoped>
.no-arrow::-webkit-inner-spin-button,
.no-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-arrow {
  -moz-appearance: textfield;
}

.was-validated .form-control:invalid,
.form-control.is-invalid {
  background-image: none !important;
  padding: 0 !important;
}
</style>
